import { useState, useEffect } from "react";
import { Trash2, Lock, Zap, ArrowRight, CheckCircle, XCircle, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { apiGetPredictions, apiCancelPrediction, getPoints, apiGetFixtures } from "@/lib/storage";
import { getSportEmoji, isFixtureLocked, formatDateShort } from "@/lib/fixtures";
import { UCL_LOGO, KCL_LOGO } from "@/components/Layout";
import { toast } from "sonner";

const POINTS_PER_CORRECT_PICK = 10;

export default function Predictions() {
  const navigate = useNavigate();
  const [predictions, setPredictions] = useState([]);
  const [points, setPoints] = useState(getPoints());
  const [fixtures, setFixtures] = useState([]);

  const refresh = async () => {
    const preds = await apiGetPredictions();
    setPredictions(preds);
    setPoints(getPoints());
  };

  useEffect(() => {
    refresh();
    apiGetFixtures().then(setFixtures);
    const handler = () => refresh();
    window.addEventListener("predictions-updated", handler);
    return () => window.removeEventListener("predictions-updated", handler);
  }, []);

  const wonPreds = predictions.filter(p => p.status === "won");
  const lostPreds = predictions.filter(p => p.status === "lost");
  const pendingPreds = predictions.filter(p => !p.status || p.status === "pending");
  const totalPointsEarned = wonPreds.length * POINTS_PER_CORRECT_PICK;

  const handleCancel = async (fixtureId) => {
    const fixture = fixtures.find(f => f.id === fixtureId);
    if (fixture && isFixtureLocked(fixture)) { toast.error("Cannot cancel — fixture locked!"); return; }
    try {
      await apiCancelPrediction(fixtureId);
      toast.success("Prediction cancelled!");
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const statusBadge = (pred) => {
    if (pred.status === "won") return <Badge className="text-[10px] bg-green-500/10 text-green-400 border-green-500/30 gap-1"><CheckCircle size={8} /> CORRECT +{POINTS_PER_CORRECT_PICK}</Badge>;
    if (pred.status === "lost") return <Badge className="text-[10px] bg-red-500/10 text-red-400 border-red-500/30 gap-1"><XCircle size={8} /> INCORRECT</Badge>;
    const fixture = fixtures.find(f => f.id === pred.fixture_id);
    if (fixture && isFixtureLocked(fixture)) return <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400 gap-1"><Lock size={8} /> AWAITING</Badge>;
    return <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400 gap-1"><Clock size={8} /> PENDING</Badge>;
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="predictions-page">
      <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>MY PREDICTIONS</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-[#111827] border border-white/5 rounded-sm p-4">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest">Picks Made</p>
          <p className="text-2xl font-bold font-mono text-white" data-testid="total-picks">{predictions.length}</p>
        </div>
        <div className="bg-[#111827] border border-white/5 rounded-sm p-4">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest">Points Earned</p>
          <p className="text-2xl font-bold font-mono text-green-400" data-testid="total-won">{totalPointsEarned}</p>
        </div>
        <div className="bg-[#111827] border border-white/5 rounded-sm p-4">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest">Record</p>
          <p className="text-lg font-bold font-mono" data-testid="win-loss-record">
            <span className="text-green-400">{wonPreds.length}W</span>
            <span className="text-slate-600 mx-1">-</span>
            <span className="text-red-400">{lostPreds.length}L</span>
            <span className="text-slate-600 mx-1">-</span>
            <span className="text-slate-400">{pendingPreds.length}P</span>
          </p>
        </div>
        <div className="bg-[#111827] border border-white/5 rounded-sm p-4">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest">Total Points</p>
          <p className="text-2xl font-bold font-mono text-white" data-testid="total-points">{points}</p>
        </div>
      </div>
      {predictions.length === 0 ? (
        <div className="bg-[#111827] border border-white/5 rounded-sm p-12 text-center" data-testid="no-predictions">
          <Zap size={24} className="mx-auto text-slate-600 mb-3" />
          <p className="text-xs text-slate-500 mb-4">No predictions yet.</p>
          <button data-testid="go-to-fixtures-btn" onClick={() => navigate("/fixtures")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider rounded-sm transition-colors">
            Browse Fixtures <ArrowRight size={12} />
          </button>
        </div>
      ) : (
        <div className="bg-[#111827] border border-white/5 rounded-sm overflow-hidden">
          <div className="red-gradient-line" />
          {predictions.map(pred => {
            const fixture = fixtures.find(f => f.id === pred.fixture_id);
            if (!fixture) return null;
            const locked = isFixtureLocked(fixture);
            const isScored = pred.status === "won" || pred.status === "lost";
            return (
              <div key={pred.fixture_id} className={`match-row ${locked && !isScored ? "opacity-60" : ""} ${pred.status === "won" ? "bg-green-500/[0.03]" : pred.status === "lost" ? "bg-red-500/[0.03]" : ""}`} data-testid={`prediction-${pred.fixture_id}`}>
                <span className="text-base mr-3">{getSportEmoji(fixture.sport)}</span>
                <div className="flex-1 min-w-0 mr-4">
                  <p className="text-xs font-semibold text-white truncate">{fixture.sport}</p>
                  <p className="text-[10px] text-slate-500">{formatDateShort(fixture.date)} | {fixture.time}</p>
                </div>
                <div className="flex items-center gap-1.5 mr-4">
                  <img src={pred.team === "A" ? UCL_LOGO : KCL_LOGO} alt="" className="w-5 h-5 object-contain" />
                  <span className="text-xs font-bold text-white">{pred.team === "A" ? "UCL" : "KCL"}</span>
                </div>
                <div className="text-right mr-4">
                  <p className="text-xs font-mono text-green-400 font-bold">+{POINTS_PER_CORRECT_PICK}</p>
                  <p className="text-[9px] text-slate-600 font-mono">pts if correct</p>
                </div>
                {statusBadge(pred)}
                {!isScored && !locked && (
                  <button data-testid={`cancel-prediction-${pred.fixture_id}`} onClick={() => handleCancel(pred.fixture_id)}
                    className="p-1.5 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400 transition-colors ml-2">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
